import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useUploadedData } from 'src/hooks/useUploadedData';
import { useAppContext } from 'src/contexts/AppContext';
import { UploadedDataResponse } from 'src/types';
import {
  getAvailableMetrics,
  getAvailableMediaSources,
  transformDataToDataSets,
} from 'src/features/DataUpload/utils/dataVisualizationHelpers';
import { DataSetPanel } from './components/DataSetPanel';
import { DataVisualizationActions } from './components/DataVisualizationActions';
import { ErrorNotifBox } from 'src/components/atoms/ErrorNotifBox';

type DataVisualizationProps = {
  fileIds?: string[];
  showInternalLoading?: boolean;
  preloadedData?: UploadedDataResponse['data'] | null;
  onResetToUpload?: () => void;
  uploadedFileNames?: string[];
  onUpload?: () => void;
  onDeleteUploadedData?: () => void;
  isPreviewMode?: boolean;
  isUploading?: boolean;
};
const DataVisualization: React.FC<DataVisualizationProps> = ({
  fileIds = [],
  showInternalLoading = true,
  preloadedData,
  onResetToUpload,
  onUpload,
  onDeleteUploadedData,
  isPreviewMode = false,
  isUploading = false,
}) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName || '';
  const previewNoticeBg = 'statusBg.running';

  const {
    data: uploadedData,
    loading,
    error,
  } = useUploadedData({
    customerId,
    fileIds,
    dataType: 'all',
    enabled:
      !preloadedData && !!customerId && fileIds.length > 0 && !isPreviewMode,
  });

  // Use preloaded data if available, otherwise use fetched data
  const data = preloadedData || uploadedData;
  const isLoading = preloadedData ? false : loading;

  // Available metrics for each data type
  const availableMetrics = useMemo(
    () => getAvailableMetrics(data || null),
    [data],
  );

  // Available media/sources for each data type
  const availableMediaSources = useMemo(
    () => getAvailableMediaSources(data || null),
    [data],
  );

  // Selected metrics state for each data type
  const [selectedMetrics, setSelectedMetrics] = useState<
    Record<string, string>
  >({
    daily: 'spend',
    weekly: 'spend',
    monthly: 'spend',
    offline: 'spend',
    kpi: 'sales',
  });

  // KPI metric columns are dynamic — keep the selection valid once the
  // uploaded data reveals which metrics exist
  useEffect(() => {
    const kpiMetrics = availableMetrics.kpi || [];
    if (kpiMetrics.length > 0 && !kpiMetrics.includes(selectedMetrics.kpi)) {
      setSelectedMetrics((prev) => ({ ...prev, kpi: kpiMetrics[0] }));
    }
  }, [availableMetrics, selectedMetrics.kpi]);

  // Selected media/source state for each data type
  const [selectedMediaSources, setSelectedMediaSources] = useState<
    Record<string, string>
  >({
    daily: 'all',
    weekly: 'all',
    monthly: 'all',
    offline: 'all',
  });

  // Handler for metric selection changes
  const handleMetricChange =
    (dataType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMetrics((prev) => ({
        ...prev,
        [dataType]: e.target.value,
      }));
    };

  // Handler for media/source selection changes
  const handleMediaSourceChange =
    (dataType: string) => (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMediaSources((prev) => ({
        ...prev,
        [dataType]: e.target.value,
      }));
    };

  // Transform uploaded data into DataSet format for charts
  const dataSets = useMemo(
    () =>
      transformDataToDataSets(
        data || null,
        selectedMetrics,
        selectedMediaSources,
      ),
    [data, selectedMetrics, selectedMediaSources],
  );

  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    new Set(dataSets.filter((d) => d.isExpanded).map((d) => d.id)),
  );

  const toggleItem = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  // Customer ID validation
  if (!customerId) {
    return (
      <Box w="100%" maxW="876px">
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>Customer not found</AlertTitle>
          <AlertDescription>
            Unable to load data. Please ensure you&apos;re logged in with a
            valid customer account.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  // Loading state (only show if enabled)
  if (isLoading && showInternalLoading) {
    return (
      <Box
        w="100%"
        maxW="876px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px">
        <VStack>
          <Spinner speed="0.8s" size="xl" thickness="4px" />
          <Text>Loading uploaded data...</Text>
        </VStack>
      </Box>
    );
  }

  // If loading but internal loading is disabled, show nothing (parent handles loading)
  if (isLoading && !showInternalLoading) {
    return null;
  }

  // Error state
  if (error) {
    return (
      <Box w="100%" maxW="876px">
        <ErrorNotifBox
          title="Error loading data!"
          description={typeof error === 'string' ? error : undefined}
        />
      </Box>
    );
  }

  // No data state
  if (!data || dataSets.length === 0) {
    return (
      <Box w="100%" maxW="876px">
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>No data found</AlertTitle>
          <AlertDescription>
            No data was found for this file. The file may not have been
            processed yet.
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box w="100%">
      {isPreviewMode && (
        <Alert
          status="info"
          bg={previewNoticeBg}
          color="content.title"
          borderRadius="lg"
          mb={4}>
          <AlertIcon color="status.text.running" />
          <AlertDescription>
            Preview only — nothing has been uploaded yet. Review the charts
            below. If everything looks correct, click &quot;Confirm &amp;
            Upload&quot; to upload your data.
          </AlertDescription>
        </Alert>
      )}
      <VStack spacing={3} align="stretch">
        {dataSets.map((dataSet, index) => (
          <DataSetPanel
            key={dataSet.id}
            dataSet={dataSet}
            isExpanded={expandedItems.has(dataSet.id)}
            isLastItem={index === dataSets.length - 1}
            onToggle={toggleItem}
            availableMetrics={availableMetrics}
            availableMediaSources={availableMediaSources}
            selectedMetrics={selectedMetrics}
            selectedMediaSources={selectedMediaSources}
            onMetricChange={handleMetricChange}
            onMediaSourceChange={handleMediaSourceChange}
          />
        ))}
      </VStack>

      <DataVisualizationActions
        isPreviewMode={isPreviewMode}
        onResetToUpload={onResetToUpload}
        onUpload={onUpload}
        onDeleteUploadedData={onDeleteUploadedData}
        isUploading={Boolean(isUploading)}
      />
    </Box>
  );
};
export default DataVisualization;
